import { useState } from "react";
import { Minus, Plus, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface PassengerCounts {
  adults: number;
  children: number;
  infants: number;
}

interface PassengerSelectorProps {
  value: PassengerCounts;
  onChange: (counts: PassengerCounts) => void;
  maxPassengers?: number;
}

const PassengerSelector = ({ 
  value, 
  onChange, 
  maxPassengers = 9 
}: PassengerSelectorProps) => {
  const [open, setOpen] = useState(false);

  const passengerTypes = [
    {
      key: "adults" as const,
      label: "Adultos",
      description: "De 12 años a más",
      min: 1,
    },
    {
      key: "children" as const,
      label: "Niños",
      description: "De 2 hasta 11 años",
      min: 0,
    },
    {
      key: "infants" as const,
      label: "Infantes",
      description: "Hasta 23 meses",
      min: 0,
    },
  ];

  const totalPassengers = value.adults + value.children + value.infants;

  const handleIncrement = (key: keyof PassengerCounts) => {
    if (totalPassengers >= maxPassengers) return;
    onChange({ ...value, [key]: value[key] + 1 });
  };

  const handleDecrement = (key: keyof PassengerCounts) => {
    const type = passengerTypes.find((t) => t.key === key);
    if (!type || value[key] <= type.min) return;
    onChange({ ...value, [key]: value[key] - 1 });
  };

  const getDisplayText = () => {
    const parts: string[] = [];
    if (value.adults > 0) {
      parts.push(`${value.adults} ${value.adults === 1 ? "adulto" : "adultos"}`);
    }
    if (value.children > 0) {
      parts.push(`${value.children} ${value.children === 1 ? "niño" : "niños"}`);
    }
    if (value.infants > 0) {
      parts.push(`${value.infants} ${value.infants === 1 ? "infante" : "infantes"}`);
    }
    return parts.join(", ");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative w-full px-4 py-3 pl-10 border-2 border-border rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left text-foreground"
          )}
          aria-label="Seleccionar pasajeros"
        >
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          {getDisplayText()}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4 bg-popover border border-border" align="start">
        <div className="space-y-4">
          {passengerTypes.map((type) => {
            const count = value[type.key];
            const isMinDisabled = count <= type.min;
            const isMaxDisabled = totalPassengers >= maxPassengers;

            return (
              <div
                key={type.key}
                className="flex items-center justify-between"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {type.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {type.description}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleDecrement(type.key)}
                    disabled={isMinDisabled}
                    className={cn(
                      "w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors",
                      isMinDisabled
                        ? "border-muted text-muted-foreground cursor-not-allowed opacity-50"
                        : "border-secondary text-secondary hover:bg-secondary/10"
                    )}
                    aria-label={`Disminuir ${type.label.toLowerCase()}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-6 text-center text-sm font-semibold text-foreground tabular-nums">
                    {count}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleIncrement(type.key)}
                    disabled={isMaxDisabled}
                    className={cn(
                      "w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors",
                      isMaxDisabled
                        ? "border-muted text-muted-foreground cursor-not-allowed opacity-50"
                        : "border-secondary text-secondary hover:bg-secondary/10"
                    )}
                    aria-label={`Aumentar ${type.label.toLowerCase()}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          {totalPassengers >= maxPassengers && (
            <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
              Máximo {maxPassengers} pasajeros permitidos
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PassengerSelector;
